/** Vocabulário e helpers usados pela lista (cards) E pela página de
 *  detalhe de upload — separados pra nenhuma das duas importar a outra. */

import type { UploadSummary } from "@/shared/api/api-client";

export const STATUS_META: Record<string, { label: string; tone: string }> = {
  UPLOADED: { label: "Na fila", tone: "" },
  STARTED: { label: "Processando", tone: "" },
  FINISHED: { label: "Concluído", tone: "success" },
  FINISHED_WITH_WARNINGS: { label: "Concluído com pendências", tone: "warning" },
  ERRORED: { label: "Erro", tone: "warning" },
  INVALID_TEMPLATE: { label: "Modelo inválido", tone: "warning" },
  // O leitor recusou o arquivo (não é planilha, é comprimido, estoura o
  // teto). Sem bronze, então sem download — ver `canDownload`.
  INVALID_FILE: { label: "Arquivo inválido", tone: "warning" }
};

/** Upload que o LEITOR recusou não tem bronze: não há o que
 *  reconstruir, e o servidor responderia 404. As duas telas escondem o
 *  botão pela mesma pergunta. */
export function canDownload(upload: { status: string }): boolean {
  return upload.status !== "INVALID_FILE";
}

export const ACTIVE_STATUSES = new Set(["UPLOADED", "STARTED"]);

/** O rótulo do status, com uma correção que o status sozinho não dá.
 *
 *  `STARTED` deixou de significar "o worker está com este arquivo na
 *  mão". Desde que o upload passou a ser fatiado em faixas, quem carimba
 *  `STARTED` é o planejamento — que só conta linhas e cria as faixas, em
 *  segundos. Cinco arquivos enfileirados viram `STARTED` quase juntos e
 *  apareciam os cinco como "Processando", enquanto apenas um consumia
 *  linhas de fato.
 *
 *  `is_advancing` vem do servidor justamente pra desfazer isso: um
 *  upload já fatiado mas parado na fila volta a se chamar "Na fila", que
 *  é o que ele é. */
export function statusMeta(status: string, isAdvancing = true) {
  if (status === "STARTED" && !isAdvancing) return STATUS_META.UPLOADED;
  return STATUS_META[status] ?? { label: status, tone: "" };
}

/** Como o upload se chama na tela. O nome do arquivo é o que a pessoa
 *  reconhece; o `#id · clipadora` é o reserva pra upload anterior ao
 *  registro do nome (nasceram `null` e não há de onde recuperar). */
export function uploadLabel(upload: {
  id: number;
  filename: string | null;
  clipping_service: string | null;
}): string {
  return upload.filename ?? `#${upload.id} · ${upload.clipping_service ?? "sem modelo"}`;
}

export function progressPercent(upload: UploadSummary): number {
  if (upload.status === "FINISHED" || upload.status === "FINISHED_WITH_WARNINGS") return 100;
  if (!upload.total_rows) return upload.status === "UPLOADED" ? 4 : 15;
  return Math.min(100, Math.round((upload.processed_rows / upload.total_rows) * 100));
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export type UploadIssue = { text: string; tone: "warning" | "neutral" };

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** O resumo do que ficou pra trás num upload, numa linha só (2026-10-19).
 *
 *  Eram até quatro pílulas empilhadas que se repetiam: desde 2026-09-14 a
 *  linha sem termo de marca vai pra fila, então "N sem termo de marca" já
 *  está dentro de "N pra corrigir", e "erros" conta TENTATIVAS, não
 *  linhas. A linha mostra o que pede ação (a fila) e o que explica o gap
 *  (já existiam); a quebra completa vai no `title`.
 *
 *  - Com fila pendente, ela é o aviso; os erros não aparecem à parte.
 *  - Sem fila, os erros aparecem (é o upload que falhou inteiro, ou cuja
 *    fila já foi resolvida e só sobrou o registro).
 *  - Sem fila, "sem termo de marca" é o PULO antigo (anterior à regra),
 *    que não tem o que corrigir: sai neutro. */
export function uploadIssues(upload: UploadSummary): { issues: UploadIssue[]; title: string } {
  const issues: UploadIssue[] = [];
  const pending = upload.pending_correction_count;
  if (pending > 0) {
    issues.push({ text: `${pending} pra corrigir`, tone: "warning" });
  } else if (upload.error_count > 0) {
    issues.push({ text: plural(upload.error_count, "erro", "erros"), tone: "warning" });
  }
  if (pending === 0 && upload.no_brand_match_count > 0) {
    issues.push({ text: `${upload.no_brand_match_count} sem termo de marca`, tone: "neutral" });
  }
  if (upload.already_ingested_count > 0) {
    issues.push({ text: `${upload.already_ingested_count} já existiam`, tone: "neutral" });
  }
  const title = [
    pending > 0 ? `${pending} na fila de correção` : null,
    upload.no_brand_match_count > 0 ? `${upload.no_brand_match_count} sem termo de marca` : null,
    upload.error_count > 0 ? plural(upload.error_count, "tentativa com erro", "tentativas com erro") : null,
    upload.already_ingested_count > 0 ? `${upload.already_ingested_count} já existiam` : null
  ]
    .filter(Boolean)
    .join(" · ");
  return { issues, title };
}
